import { Flex, Image, useColorMode } from '@chakra-ui/react';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import {
    getLocationSelectPagePath,
    getLoginFormPagePath,
    getOTPEntryPagePath,
} from '../../App';
import {
    fetchPauseBettorAccount,
    fetchRefreshAllBettorAccounts,
    fetchRefreshBettorAccount,
    fetchRemoveBettorAccount,
} from '../../api';
import Button from '../../components/button';
import InfoCard from '../../components/info-card';
import Layout from '../../components/layout';
import Text from '../../components/text';
import { useSession } from '../../hooks/useSession';
import Logos from '../../images/logos';
import SwalService from '../../services/swal';
import { BookStatuses, IUseGetContextResData } from '../../types';

enum ToggleStates {
    Verified,
    Paused,
    Unverified,
    Unverifiable,
}

const getToggleState = (
    verified: boolean,
    paused: boolean,
    isUnverifiable: boolean
) => {
    if (verified && !paused) {
        return ToggleStates.Verified;
    } else if (verified && paused) {
        return ToggleStates.Paused;
    } else if (!verified && !isUnverifiable) {
        return ToggleStates.Unverified;
    } else if (!verified && isUnverifiable) {
        return ToggleStates.Unverifiable;
    } else {
        return null;
    }
};

export default function AccountInfo() {
    const session = useSession();
    const [extensionVersion, setExtensionVersion] = useState<string | null>(
        null
    );
    const [goingToRefresh, setGoingToRefresh] = useState<boolean>(false);
    const [localRefreshStates, setLocalRefreshStates] = useState<{[key: string]: boolean}>({});
    const isAuthContext = !!session?.auth;
    const { push } = useHistory();
    const { colorMode, toggleColorMode } = useColorMode();

    const bettorAccounts =
        session?.bettorAccounts?.sort((a, b) =>
            a.book?.name?.localeCompare(b.book?.name)
        ) || [];

    const bettorAccountRefreshesInProgress = bettorAccounts?.filter(
        ({ refreshInProgress }) => {
            return refreshInProgress;
        }
    );


    useEffect(() => {
        if (
            session?.uiMode &&
            session?.uiMode != 'system' &&
            session?.uiMode != colorMode
        )
            toggleColorMode();
    }, []);

    useEffect(() => {
        setExtensionVersion(
            sessionStorage.getItem('sharpSportsExtensionVersion')
        );
    }, []);

    useEffect(() => {
        if (!!bettorAccountRefreshesInProgress?.length && !goingToRefresh) {
            setGoingToRefresh(true);
            setTimeout(() => {
                setGoingToRefresh(false);
                session?.fetchContext(true);
            }, 2000);
        }
    });

    // Clear local refresh states when server-side refreshInProgress becomes true
    useEffect(() => {
        const updatedLocalStates = { ...localRefreshStates };
        let hasChanges = false;
        
        bettorAccounts.forEach(account => {
            if (account.refreshInProgress && localRefreshStates[account.id]) {
                delete updatedLocalStates[account.id];
                hasChanges = true;
            }
        });
        
        if (hasChanges) {
            setLocalRefreshStates(updatedLocalStates);
        }
    }, [bettorAccounts, localRefreshStates]);

    // 3-second fallback to clear all local refresh states
    useEffect(() => {
        if (Object.keys(localRefreshStates).length > 0) {
            const timeout = setTimeout(() => {
                setLocalRefreshStates({});
            }, 3000);
            
            return () => clearTimeout(timeout);
        }
    }, [localRefreshStates]);

    useEffect(() => {
        if (extensionVersion != session?.extensionVersion) {
            var extensionVersionResponse = fetch(
                `${process.env.REACT_APP_API_BASE || ''}/v1/context/?cid=${
                    session?.cid
                }`,
                {
                    method: 'PUT',
                    headers: {
                        Authorization: `Token ${process.env.REACT_APP_SUPERUSER_API_KEY}`,
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        extensionVersion: extensionVersion,
                    }),
                }
            );
        }
    }, [extensionVersion]);

    useEffect(() => {
        let token = session?.token;
        const initEvent = new CustomEvent('initSharpSportsExtension', {
            detail: {
                internalId: session?.internalId,
                publicKey: token ? token.replace('Token ', '') : '',
                extensionAuthToken: session?.extensionAuthToken,
            },
        });
        document.dispatchEvent(initEvent);
    }, []);


    return (
        <Layout
            colorMode={colorMode}
            alert={session?.user?.isSandbox ? 'SANDBOX MODE' : ''}
            // alertSecondary={
            //     session?.user?.isSandbox
            //         ? 'For SDK required books please test in live mode and follow implementation instructions in the docs'
            //         : ''
            // }
            buttons={
                <>
                    <Button
                        variant="secondary"
                        onClick={async () => {
                            // Immediately set all accounts to refreshing state
                            const allAccountStates = bettorAccounts.reduce((acc, account) => {
                                acc[account.id] = true;
                                return acc;
                            }, {} as {[key: string]: boolean});
                            setLocalRefreshStates(allAccountStates);
                            
                            const response =
                                await fetchRefreshAllBettorAccounts(
                                    session?.internalId!,
                                    session?.user?.tokens?.public!,
                                    session?.auth ||
                                        session?.extensionAuthToken!,
                                    session?.service!
                                );
                            if (response?.ok) {
                                session?.fetchContext(true);
                                let data = await response.json();
                                if (data?.otpRequired.length > 0) {
                                    push(
                                        getOTPEntryPagePath(session?.cid || '')
                                    );
                                }
                            } else {
                                if (response.status === 429) {
                                    SwalService.rateLimited();
                                } else {
                                    SwalService.errorSwal('There was an error refreshing this account. Please try again');
                                }
                            }
                        }}
                    >
                        Refresh All Accounts
                    </Button>
                    <Button
                        onClick={() => {
                            push(getLocationSelectPagePath(session?.cid!));
                        }}
                        w="full"
                    >
                        Link New Book
                    </Button>
                </>
            }
        >
            <Flex
                direction="column"
                align="center"
                gap="8px"
                w="full"
                textAlign="center"
            >
                {session?.logoUrl && (
                    <Image
                        maxH="80px"
                        maxW="200px"
                        src={session?.logoUrl || ''}
                        alt="Sharp Sports"
                    />
                )}
                <Flex direction="column" gap="10px" w="full">
                    {bettorAccounts.length === 0 && (
                        <Flex
                            direction="column"
                            align="center"
                            justify="center"
                            gap="8px"
                            w="full"
                            py="48px"
                            textAlign="center"
                        >
                            <Text fontWeight="600">No linked accounts yet</Text>
                            <Text size="sm" opacity={0.7}>
                                Link a book to start syncing your bets.
                            </Text>
                        </Flex>
                    )}
                    {bettorAccounts.map(
                        (
                            {
                                id,
                                balance,
                                book,
                                latestRefreshResponse,
                                isUnverifiable,
                                verified,
                                paused,
                                bookRegion,
                                refreshInProgress,
                            },
                            idx
                        ) => {
                            let refreshError =
                                latestRefreshResponse?.status > 204
                                    ? latestRefreshResponse?.detail || null
                                    : null;
                            if (
                                latestRefreshResponse?.status == 424 &&
                                latestRefreshResponse?.status > 204 &&
                                bookRegion.status != 'inactive'
                            ) {
                                refreshError =
                                    'There was an issue refreshing this account, please try again';
                            }
                            const toggleState = getToggleState(
                                verified,
                                paused,
                                isUnverifiable
                            );
                            const isToggleOn =
                                toggleState === ToggleStates.Verified;

                            const getSupportedExtensions = () => {
                                let bookSupport = session?.sdkSupport[book.id];
                                let supportedExtensions =
                                    bookSupport?.webBrowserExtension.platforms.filter(
                                        (x: any) => {
                                            return x.supported === true;
                                        }
                                    );
                                return supportedExtensions ?? [];
                            };

                            const getTooltipDisabled = () => {
                                if (isAuthContext) return true;

                                let bookSupport = session?.sdkSupport[book.id];
                                if (bookSupport) {
                                    let supportedExtensions =
                                        bookSupport.webBrowserExtension.platforms.filter(
                                            (x: any) => {
                                                return x.supported === true;
                                            }
                                        );
                                    if (
                                        !session?.extensionAuthToken &&
                                        supportedExtensions.length > 0
                                    )
                                        return false;
                                    if (
                                        !isAuthContext &&
                                        supportedExtensions.length == 0
                                    )
                                        return false;
                                }
                                return true;
                            };

                            const getRefreshDisabled = () => {
                                if (refreshInProgress) return true;
                                if (!isToggleOn) return true;
                                if (isAuthContext) return false; //all books should show up in mobile refresh

                                let bookSupport = session?.sdkSupport[book.id];
                                if (bookSupport) {
                                    let supportedExtensions =
                                        bookSupport.webBrowserExtension.platforms.filter(
                                            (x: any) => {
                                                return x.supported === true;
                                            }
                                        );
                                    //if no extensionAuthToken and book webBrowserExtension supported = true (any platform) return true
                                    if (
                                        !session?.extensionAuthToken &&
                                        supportedExtensions.length > 0
                                    )
                                        return true; //no extension auth token then extension books disabled
                                    //if no authContext and yes bookSdkSupport (implies sdk only) and browser supported = false (all platforms) return true
                                    if (
                                        !isAuthContext &&
                                        supportedExtensions.length == 0
                                    )
                                        return true;
                                }

                                return false;
                            };

                            const handleClickRefreshButton = async () => {
                                let popupText = `Download the SharpSports Browser Extension to Complete the Link. Available on Chrome Browser Only`;
                                //case of download extension;
                                let bookSupport = session?.sdkSupport[book.id];
                                let supportedExtensions =
                                    bookSupport?.webBrowserExtension.platforms.filter(
                                        (x: any) => {
                                            return x.supported === true;
                                        }
                                    ) ?? [];
                                if (
                                    !isAuthContext &&
                                    !extensionVersion &&
                                    supportedExtensions.length > 0
                                ) {
                                    {
                                        SwalService.downloadExtensionSwal().then(
                                            (result) => {
                                                if (result.isConfirmed) {
                                                    window.open(
                                                        'https://chrome.google.com/webstore/detail/sharpsports/jbjkingkpiakebpokhndnnmniaafoobe',
                                                        '_blank',
                                                        'noopener,noreferrer'
                                                    );
                                                }
                                            }
                                        );
                                    }
                                    return;
                                }

                                // Immediately set this account to refreshing state
                                setLocalRefreshStates(prev => ({...prev, [id]: true}));

                                const response =
                                    await fetchRefreshBettorAccount(
                                        id,
                                        session?.user?.tokens?.public!,
                                        false,
                                        session?.auth ||
                                            session?.extensionAuthToken!,
                                        session?.service!
                                    );
                                if (response?.ok) {
                                    let data = await response.json();
                                    if (data?.rateLimited.length > 0) {
                                        return SwalService.rateLimited();
                                    } else if (data?.otpRequired.length > 0) {
                                        session?.fetchContext(true);
                                        push(
                                            getOTPEntryPagePath(session?.cid!)
                                        );
                                    } else {
                                        session?.fetchContext(true);
                                    }
                                } else {
                                    SwalService.errorSwal('Something went wrong');
                                }
                            };

                            const handleChangeToggle = async () => {
                                let response;
                                let updatedBettorAccounts;
                                if (toggleState === ToggleStates.Verified) {
                                    response = await fetchPauseBettorAccount(
                                        id,
                                        session?.user?.tokens?.public!,
                                        true
                                    );
                                    if (response.ok) {
                                        updatedBettorAccounts =
                                            bettorAccounts.map((bAcc) => {
                                                if (bAcc.id === id) {
                                                    return {
                                                        ...bAcc,
                                                        paused: true,
                                                    };
                                                }
                                                return bAcc;
                                            });
                                        return session?.setContext({
                                            ...session,
                                            bettorAccounts:
                                                updatedBettorAccounts,
                                        } as IUseGetContextResData);
                                    } else {
                                        return SwalService.errorSwal(
                                            'Something went wrong'
                                        );
                                    }
                                } else if (
                                    toggleState === ToggleStates.Paused
                                ) {
                                    response = await fetchPauseBettorAccount(
                                        id,
                                        session?.user?.tokens?.public!,
                                        false
                                    );
                                    if (response.ok) {
                                        updatedBettorAccounts =
                                            bettorAccounts.map((bAcc) => {
                                                if (bAcc.id === id) {
                                                    return {
                                                        ...bAcc,
                                                        paused: false,
                                                    };
                                                }
                                                return bAcc;
                                            });
                                        return session?.setContext({
                                            ...session,
                                            bettorAccounts:
                                                updatedBettorAccounts,
                                        } as IUseGetContextResData);
                                    } else {
                                        return SwalService.errorSwal(
                                            'Something went wrong'
                                        );
                                    }
                                } else if (
                                    toggleState === ToggleStates.Unverified
                                ) {
                                    // Immediately set this account to refreshing state
                                    setLocalRefreshStates(prev => ({...prev, [id]: true}));
                                    
                                    response = await fetchRefreshBettorAccount(
                                        id,
                                        session?.user?.tokens?.public!,
                                        true,
                                        session?.auth ||
                                            session?.extensionAuthToken!,
                                        session?.service!
                                    );
                                    if (response?.ok) {
                                        let data = await response.json();
                                        if (data?.rateLimited.length > 0) {
                                            return SwalService.rateLimited();
                                        } else if (
                                            data?.otpRequired.length > 0
                                        ) {
                                            session?.fetchContext(true);
                                            push(
                                                getOTPEntryPagePath(
                                                    session?.cid!
                                                )
                                            );
                                        } else {
                                            session?.fetchContext(true);
                                        }
                                    } else {
                                        if (response.status === 429) {
                                            SwalService.rateLimited();
                                        } else {
                                            SwalService.errorSwal(
                                                'There was an error refreshing this account. Please try again'
                                            );
                                        }
                                    }
                                } else if (
                                    toggleState === ToggleStates.Unverifiable
                                ) {
                                    if (
                                        bookRegion?.status !==
                                        BookStatuses.Active
                                    ) {
                                        return SwalService.inactiveBook(
                                            'This book'
                                        );
                                    } else {
                                        return push(
                                            getLoginFormPagePath(
                                                session?.cid || '',
                                                bookRegion.id
                                            )
                                        );
                                    }
                                } else {
                                    console.warn(
                                        'toggleState is null',
                                        toggleState,
                                        bettorAccounts[idx]
                                    );
                                }
                            };

                            const handleClickRemove = () => {
                                SwalService.deleteAccount().then((result) => {
                                    if (result.isConfirmed) {
                                        var churnReason = result.value;
                                        fetchRemoveBettorAccount(
                                            id,
                                            session?.user?.tokens?.public!,
                                            churnReason
                                        ).then(() => {
                                            session?.fetchContext();
                                        });
                                    }
                                });
                            };

                            return (
                                <InfoCard
                                    key={id}
                                    balance={balance}
                                    logo={Logos[colorMode][book.abbr]}
                                    onSelect={(selected) =>
                                        handleChangeToggle()
                                    }
                                    onDelete={handleClickRemove}
                                    onRefresh={handleClickRefreshButton}
                                    isChecked={isToggleOn}
                                    isRefreshDisabled={getRefreshDisabled()}
                                    isToolTipDisabled={getTooltipDisabled()}
                                    supportedExtensions={getSupportedExtensions()}
                                    username={session?.user?.name || ''}
                                    bookname={book.name}
                                    bookRegionName={bookRegion?.name}
                                    refreshInProgress={refreshInProgress || localRefreshStates[id] || false}
                                    lastUpdate={moment(
                                        latestRefreshResponse?.timeCreated
                                    ).fromNow()}
                                    errorMsg={
                                        refreshInProgress ? null : refreshError
                                    }
                                    colorMode={colorMode}
                                    extensionAuthToken={session?.extensionAuthToken || null}
                                />
                            );
                        }
                    )}
                </Flex>
            </Flex>
        </Layout>
    );
}
